import React, { useState, useEffect, useRef, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import apiClient from "../../utils/apiClient";
import { useNavigate } from "react-router-dom";
import { getCurrentUser } from "../../utils/auth";
import CreatableSelect from "react-select/creatable";
import {
  Mic, Upload, Clock, BarChart3, Brain, Play,
  FileText, ChevronRight, Sparkles, Target, Award,
  Calendar, Trash2, CheckCircle, AlertCircle,
  Wifi, Globe, Loader2, X, Volume2, ChevronDown,
} from "lucide-react";
import {
  PageHeader, StatsCard, AppButton, AppBadge,
  AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  Tabs, TabsList, TabsTrigger, TabsContent,
  Separator, EmptyState,
} from "../ui";
import "./mock-interview.scss";

/* ─── Constants ─── */

const INTERVIEW_TYPES = [
  { value: "Technical", label: "Technical Interview", description: "Data structures, algorithms, and technology-specific questions", icon: Brain },
  { value: "HR", label: "HR Interview", description: "Behavioral questions, culture fit, and career goals", icon: Target },
  { value: "System Design", label: "System Design", description: "Architecture, scalability, and design patterns", icon: BarChart3 },
];

const TECHNOLOGIES = {
  Technical: [
    "React.js", "Node.js", "JavaScript", "TypeScript", "Python", "Java",
    "Spring Boot", "SQL", "Angular", "HTML/CSS", "Docker", "Kubernetes",
    "AWS", "Machine Learning", "Data Science", "DevOps", "VLSI",
    "IT Support", "Medical/Healthcare", "Networking", "Cybersecurity",
    "Embedded Systems", "Cloud Computing", "Microservices", "Kafka",
  ],
  HR: ["General"],
  "System Design": [
    "React.js", "Node.js", "JavaScript", "Python", "Java",
    "Microservices", "Cloud Architecture", "Database Design",
  ],
};

const ROLES = [
  "Frontend Developer", "Backend Developer", "Full-Stack Developer",
  "Software Developer", "Data Engineer", "DevOps Engineer",
  "QA Engineer", "IT Support Specialist", "Network Engineer",
  "Cloud Architect", "Machine Learning Engineer", "VLSI Engineer",
  "Embedded Systems Engineer", "Medical Technician", "Cybersecurity Analyst",
  "System Administrator", "Database Administrator", "Mobile Developer",
  "Data Scientist", "Product Manager",
];

const DIFFICULTIES = ["Beginner", "Intermediate", "Advanced"];

const DURATIONS = [
  { value: 5, label: "5 mins (Quick)" },
  { value: 10, label: "10 mins (Short)" },
  { value: 15, label: "15 mins (Quick)" },
  { value: 20, label: "20 mins (Standard)" },
  { value: 30, label: "30 mins (Standard)" },
];

const selectStyles = {
  control: (base) => ({
    ...base,
    background: "var(--input-bg, var(--bg-primary))",
    borderColor: "var(--input-border)",
    borderRadius: 8,
    minHeight: 42,
    fontSize: "0.875rem",
    boxShadow: "none",
    "&:hover": { borderColor: "var(--primary-color)" },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    background: "var(--card-bg, var(--bg-primary))",
    border: "1px solid var(--border-color)",
    borderRadius: 10,
    overflow: "hidden",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    background: state.isFocused
      ? "var(--bg-hover, var(--bg-secondary))"
      : "transparent",
    color: "var(--text-primary)",
    fontSize: "0.85rem",
    "&:active": { background: "var(--bg-tertiary)" },
  }),
  singleValue: (base) => ({ ...base, color: "var(--text-primary)" }),
  input: (base) => ({ ...base, color: "var(--text-primary)" }),
  placeholder: (base) => ({ ...base, color: "var(--text-secondary)", fontSize: "0.85rem" }),
  multiValue: (base) => ({ ...base, background: "hsl(var(--primary, 217 91% 60%) / 0.1)", borderRadius: 6 }),
  multiValueLabel: (base) => ({ ...base, color: "hsl(var(--primary, 217 91% 60%))", fontWeight: 500, fontSize: "0.8rem" }),
  multiValueRemove: (base) => ({
    ...base,
    color: "hsl(var(--primary, 217 91% 60%))",
    "&:hover": { background: "hsl(var(--primary, 217 91% 60%) / 0.2)", color: "hsl(var(--primary, 217 91% 60%))" },
  }),
};

function getScoreBgClass(score) {
  if (score >= 80) return "mis-score-bg--high";
  if (score >= 60) return "mis-score-bg--mid";
  return "mis-score-bg--low";
}

/* ─── Mic Level Bar Sub-Component ─── */
const MicLevelBar = ({ level }) => (
  <div className="mis-precheck-mic-level">
    <div className="mis-precheck-mic-level__track">
      <div
        className={`mis-precheck-mic-level__fill${level > 60 ? " mis-precheck-mic-level__fill--high" : ""}${level > 0 && level <= 60 ? " mis-precheck-mic-level__fill--normal" : ""}`}
        style={{ width: `${level}%` }}
      />
    </div>
    <span className="mis-precheck-mic-level__text">
      {level > 0 ? "Speak to test..." : "No input detected"}
    </span>
  </div>
);

/* ─── History Tab Sub-Component ─── */

const HistoryTabContent = ({ historyLoading, history, navigate }) => {
  if (historyLoading) {
    return (
      <div className="mi-loading">
        <Spinner animation="border" />
      </div>
    );
  }

  if (history.length === 0) {
    return <EmptyState icon={<Mic size={28} />} title="No interviews yet" message="Complete your first mock interview to see your history here." />;
  }

  return (
    <div className="mis-history-list">
      {history.map((interview) => {
        const score = Math.round(interview.overallScore || 0);
        const tech = interview.config?.technology || interview.config?.topics?.[0] || "General";
        const type = interview.config?.interviewType || "Technical";
        const diff = interview.config?.difficulty || interview.config?.experience || "—";
        const dur = interview.config?.duration || "—";
        const roleName = interview.config?.role || "—";
        const qCount = interview.questions?.length || 0;

        return (
          <AppCard
            key={interview._id}
            className="mis-history-card"
            onClick={() => navigate(`/s/mock-interview/summary/${interview._id}`)}
          >
            <AppCardContent className="p-0">
              <div className="mis-history-card__row">
                <div className={`mis-history-card__score ${getScoreBgClass(score)}`}>
                  {interview.status === "completed" ? score : "—"}
                </div>
                <div className="mis-history-card__info">
                  <div className="mis-history-card__title-row">
                    <h3 className="mis-history-card__title">{tech} - {type} Interview</h3>
                    <AppBadge variant="secondary" size="sm">{diff}</AppBadge>
                  </div>
                  <div className="mis-history-card__meta">
                    <span><Target size={12} /> {roleName}</span>
                    <span><Clock size={12} /> {dur} mins</span>
                    <span>
                      <Calendar size={12} />{" "}
                      {interview.createdAt
                        ? new Date(interview.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                        : "—"}
                    </span>
                    <span><Brain size={12} /> {qCount} questions</span>
                  </div>
                </div>
                <ChevronRight size={16} className="mis-history-card__chevron" />
              </div>
            </AppCardContent>
          </AppCard>
        );
      })}
    </div>
  );
};

/* ─── Component ─── */

const MockInterviewSetup = () => {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const fileInputRef = useRef(null);

  // Form state
  const [interviewType, setInterviewType] = useState("");
  const [technology, setTechnology] = useState("");
  const [role, setRole] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [duration, setDuration] = useState("");
  const [resumeFile, setResumeFile] = useState(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Pre-interview check modal
  const [showPreCheck, setShowPreCheck] = useState(false);
  const [preChecks, setPreChecks] = useState({
    microphone: { status: "pending", label: "Microphone Access" },
    internet: { status: "pending", label: "Internet Connection" },
    browser: { status: "pending", label: "Browser Compatibility" },
  });
  const [preCheckDone, setPreCheckDone] = useState(false);
  const [preCheckAllPassed, setPreCheckAllPassed] = useState(false);

  // Device selection (Teams-style)
  const [microphones, setMicrophones] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [selectedMic, setSelectedMic] = useState("");
  const [selectedSpeaker, setSelectedSpeaker] = useState("");
  const [micLevel, setMicLevel] = useState(0);
  const micStreamRef = useRef(null);
  const micAudioCtxRef = useRef(null);
  const micRafRef = useRef(null);

  // Stats & history
  const [stats, setStats] = useState({ count: 0, avgScore: 0, bestScore: 0 });
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  /* --- Mic level monitor --- */
  const stopMicMonitor = useCallback(() => {
    if (micRafRef.current) { cancelAnimationFrame(micRafRef.current); micRafRef.current = null; }
    if (micStreamRef.current) { micStreamRef.current.getTracks().forEach((t) => t.stop()); micStreamRef.current = null; }
    if (micAudioCtxRef.current) { micAudioCtxRef.current.close().catch(() => {}); micAudioCtxRef.current = null; }
    setMicLevel(0);
  }, []);

  const startMicMonitor = useCallback(async (deviceId) => {
    stopMicMonitor();
    if (!deviceId) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: deviceId } },
      });
      micStreamRef.current = stream;
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      micAudioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.5;
      source.connect(analyser);
      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const tick = () => {
        analyser.getByteFrequencyData(dataArray);
        // RMS-ish average of bins
        let sum = 0;
        for (const val of dataArray) sum += val;
        const avg = sum / dataArray.length;
        // Normalize to 0–100
        setMicLevel(Math.min(100, Math.round((avg / 128) * 100)));
        micRafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch {
      setMicLevel(0);
    }
  }, [stopMicMonitor]);

  // Start/stop monitor when selected mic changes & modal is open
  useEffect(() => {
    if (showPreCheck && selectedMic && preCheckDone) {
      startMicMonitor(selectedMic);
    } else {
      stopMicMonitor();
    }
    return () => stopMicMonitor();
  }, [showPreCheck, selectedMic, preCheckDone, startMicMonitor, stopMicMonitor]);

  // Fetch interview history & compute stats
  useEffect(() => {
    const fetchHistory = async () => {
      setHistoryLoading(true);
      try {
        const studentId = currentUser?.studentId;
        if (!studentId) return;
        const { data } = await apiClient.get(`/mock-interview/student/${studentId}?page=1&limit=20`);
        const items = data.data || [];
        setHistory(items);
        const completed = items.filter((m) => m.status === "completed" && m.overallScore != null);
        if (completed.length > 0) {
          const avg = Math.round(completed.reduce((a, m) => a + m.overallScore, 0) / completed.length);
          const best = Math.max(...completed.map((m) => Math.round(m.overallScore)));
          setStats({ count: completed.length, avgScore: avg, bestScore: best });
        }
      } catch {
        /* ignore */
      }
      setHistoryLoading(false);
    };
    fetchHistory();
  }, [currentUser?.studentId]);

  const canStart = interviewType && technology && role && difficulty && duration;

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setResumeFile({ file, name: file.name, size: (file.size / 1024).toFixed(1) });
    }
  };

  const handleRemoveResume = () => {
    setResumeFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  /* --- Pre-interview checks --- */
  const runPreChecks = async () => {
    setPreCheckDone(false);
    setPreCheckAllPassed(false);
    const results = {
      microphone: { status: "checking", label: "Microphone Access" },
      internet: { status: "checking", label: "Internet Connection" },
      browser: { status: "checking", label: "Browser Compatibility" },
    };
    setPreChecks({ ...results });

    // 1. Microphone check + enumerate devices
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      // Enumerate devices after permission granted
      const devices = await navigator.mediaDevices.enumerateDevices();
      const mics = devices.filter((d) => d.kind === "audioinput" && d.deviceId);
      const spkrs = devices.filter((d) => d.kind === "audiooutput" && d.deviceId);
      setMicrophones(mics);
      setSpeakers(spkrs);
      if (!selectedMic && mics.length > 0) setSelectedMic(mics[0].deviceId);
      if (!selectedSpeaker && spkrs.length > 0) setSelectedSpeaker(spkrs[0].deviceId);
      results.microphone = { status: "passed", label: "Microphone Access", detail: `${mics.length} microphone${mics.length === 1 ? "" : "s"} found` };
    } catch {
      results.microphone = { status: "failed", label: "Microphone Access", detail: "Could not access microphone. Please allow permission." };
    }
    setPreChecks({ ...results });

    // 2. Internet check
    try {
      const start = Date.now();
      await fetch(`${window.location.origin}/favicon.ico?_t=${Date.now()}`, { cache: "no-store", mode: "no-cors" });
      const latency = Date.now() - start;
      results.internet = {
        status: "passed",
        label: "Internet Connection",
        detail: `Connected (${latency}ms latency)`,
      };
    } catch {
      results.internet = { status: "failed", label: "Internet Connection", detail: "No internet connection detected" };
    }
    setPreChecks({ ...results });

    // 3. Browser compatibility (SpeechSynthesis + MediaRecorder)
    const hasSpeech = "speechSynthesis" in window;
    // eslint-disable-next-line no-undef
    const hasRecorder = typeof MediaRecorder !== "undefined";
    if (hasSpeech && hasRecorder) {
      results.browser = { status: "passed", label: "Browser Compatibility", detail: "Speech & recording supported" };
    } else {
      const missing = [];
      if (!hasSpeech) missing.push("Speech Synthesis");
      if (!hasRecorder) missing.push("MediaRecorder");
      results.browser = { status: "failed", label: "Browser Compatibility", detail: `Missing: ${missing.join(", ")}. Use Chrome or Edge.` };
    }
    setPreChecks({ ...results });

    const allPassed = Object.values(results).every((c) => c.status === "passed");
    setPreCheckAllPassed(allPassed);
    setPreCheckDone(true);
  };

  const handlePreCheckOpen = () => {
    if (!canStart) return;
    setShowPreCheck(true);
    setMicLevel(0);
    // Reset and run checks
    setPreChecks({
      microphone: { status: "pending", label: "Microphone Access" },
      internet: { status: "pending", label: "Internet Connection" },
      browser: { status: "pending", label: "Browser Compatibility" },
    });
    runPreChecks();
  };

  const handleStartInterview = async () => {
    if (!canStart) return;
    setLoading(true);
    setError("");
    try {
      const durationNum = Number.parseInt(duration, 10);
      const payload = {
        topics: [technology],
        experience: difficulty,
        difficulty,
        duration: durationNum,
        interviewType,
        technology,
        role,
      };

      let formData;
      if (resumeFile?.file) {
        formData = new FormData();
        Object.entries(payload).forEach(([k, v]) => {
          if (Array.isArray(v)) v.forEach((item) => formData.append(k, item));
          else formData.append(k, v);
        });
        formData.append("resume", resumeFile.file);
      }

      const { data } = formData
        ? await apiClient.post("/mock-interview/start", formData, { headers: { "Content-Type": "multipart/form-data" } })
        : await apiClient.post("/mock-interview/start", payload);

      navigate(`/s/mock-interview/session/${data.mockInterviewId}`, {
        state: {
          firstQuestion: data.firstQuestion,
          needsCode: data.needsCode || false,
          duration: durationNum,
          technology,
          interviewType,
          role,
        },
      });
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to start interview. Please try again.");
    }
    setLoading(false);
  };

  // Build technology options based on selected interview type
  const techOptions = (TECHNOLOGIES[interviewType] || []).map((t) => ({ value: t, label: t }));

  return (
    <div className="mi-page">
      <PageHeader
        title="Mock Interview with AI"
        description="Practice interviews with an AI interviewer that adapts to your resume and skill level"
        icon={Mic}
      />

      {/* Stats row */}
      <div className="mis-stats">
        <StatsCard icon={Mic} title="Interviews Taken" value={stats.count} subtitle="Total sessions" />
        <StatsCard icon={BarChart3} title="Average Score" value={stats.count ? `${stats.avgScore}%` : "—"} subtitle="Across all interviews" iconColor="blue" />
        <StatsCard icon={Award} title="Best Score" value={stats.count ? `${stats.bestScore}%` : "—"} subtitle="Personal best" iconColor="emerald" />
        <StatsCard icon={Target} title="Improvement" value={stats.count >= 2 ? "+—" : "—"} subtitle="vs. first interview" iconColor="violet" />
      </div>

      {/* Tabs: New Interview | Past Interviews */}
      <Tabs defaultValue="setup">
        <TabsList>
          <TabsTrigger value="setup">New Interview</TabsTrigger>
          <TabsTrigger value="history">Past Interviews ({history.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="setup">
          <div className="mis-setup-grid">
            {/* Left column */}
            <div className="mis-setup-left">
              {/* Interview Type */}
              <AppCard>
                <AppCardHeader><AppCardTitle>Interview Type</AppCardTitle></AppCardHeader>
                <AppCardContent>
                  <div className="mis-type-grid">
                    {INTERVIEW_TYPES.map((type) => {
                      const Icon = type.icon;
                      const isSelected = interviewType === type.value;
                      return (
                        <button
                          key={type.value}
                          type="button"
                          className={`mis-type-card${isSelected ? " mis-type-card--active" : ""}`}
                          onClick={() => { setInterviewType(type.value); setTechnology(""); }}
                        >
                          <div className={`mis-type-card__icon${isSelected ? " mis-type-card__icon--active" : ""}`}>
                            <Icon size={16} />
                          </div>
                          <div>
                            <p className="mis-type-card__label">{type.label}</p>
                            <p className="mis-type-card__desc">{type.description}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </AppCardContent>
              </AppCard>

              {/* Configuration */}
              <AppCard>
                <AppCardHeader><AppCardTitle>Configuration</AppCardTitle></AppCardHeader>
                <AppCardContent>
                  <div className="mis-config-grid">
                    <div className="mis-form__group">
                      <span className="mis-form__label">Technology / Subject</span>
                      <CreatableSelect
                        options={techOptions}
                        value={technology ? { value: technology, label: technology } : null}
                        onChange={(opt) => setTechnology(opt?.value || "")}
                        placeholder="Select technology..."
                        isDisabled={!interviewType}
                        isClearable
                        formatCreateLabel={(v) => `Add "${v}"`}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                      />
                    </div>
                    <div className="mis-form__group">
                      <span className="mis-form__label">Target Role</span>
                      <CreatableSelect
                        options={ROLES.map((r) => ({ value: r, label: r }))}
                        value={role ? { value: role, label: role } : null}
                        onChange={(opt) => setRole(opt?.value || "")}
                        placeholder="Select role..."
                        isClearable
                        formatCreateLabel={(v) => `Add "${v}"`}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                      />
                    </div>
                    <div className="mis-form__group">
                      <label className="mis-form__label" htmlFor="mi-difficulty">Difficulty Level</label>
                      <select
                        id="mi-difficulty"
                        className="mis-form__select"
                        value={difficulty}
                        onChange={(e) => setDifficulty(e.target.value)}
                      >
                        <option value="">Select difficulty...</option>
                        {DIFFICULTIES.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                    <div className="mis-form__group">
                      <label className="mis-form__label" htmlFor="mi-duration">Duration</label>
                      <select
                        id="mi-duration"
                        className="mis-form__select"
                        value={duration}
                        onChange={(e) => setDuration(e.target.value)}
                      >
                        <option value="">Select duration...</option>
                        {DURATIONS.map((d) => (
                          <option key={d.value} value={String(d.value)}>{d.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </AppCardContent>
              </AppCard>

              {/* Resume Upload */}
              <AppCard>
                <AppCardHeader><AppCardTitle>Upload Resume (Optional)</AppCardTitle></AppCardHeader>
                <AppCardContent>
                  {resumeFile ? (
                    <div className="mis-resume-file">
                      <div className="mis-resume-file__info">
                        <div className="mis-resume-file__icon">
                          <FileText size={20} />
                        </div>
                        <div>
                          <p className="mis-resume-file__name">{resumeFile.name}</p>
                          <p className="mis-resume-file__size">{resumeFile.size} KB</p>
                        </div>
                      </div>
                      <button type="button" className="mis-resume-file__remove" onClick={handleRemoveResume}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ) : (
                    <label className="mis-resume-upload">
                      <div className="mis-resume-upload__icon">
                        <Upload size={20} />
                      </div>
                      <div className="mis-resume-upload__text">
                        <p className="mis-resume-upload__label">Upload your resume</p>
                        <p className="mis-resume-upload__hint">PDF, DOC, or DOCX up to 5MB</p>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="mis-resume-upload__input"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileUpload}
                      />
                    </label>
                  )}
                </AppCardContent>
              </AppCard>
            </div>

            {/* Right sidebar — Interview Summary */}
            <div className="mis-setup-right">
              <AppCard className="mis-summary-sidebar">
                <AppCardHeader>
                  <AppCardTitle className="mis-summary-sidebar__title">
                    <Sparkles size={16} /> Interview Summary
                  </AppCardTitle>
                </AppCardHeader>
                <AppCardContent>
                  <div className="mis-summary-rows">
                    <div className="mis-summary-row">
                      <span className="mis-summary-row__label">Type</span>
                      <span className="mis-summary-row__value">{interviewType || "Not selected"}</span>
                    </div>
                    <Separator />
                    <div className="mis-summary-row">
                      <span className="mis-summary-row__label">Technology</span>
                      <span className="mis-summary-row__value">{technology || "Not selected"}</span>
                    </div>
                    <Separator />
                    <div className="mis-summary-row">
                      <span className="mis-summary-row__label">Role</span>
                      <span className="mis-summary-row__value">{role || "Not selected"}</span>
                    </div>
                    <Separator />
                    <div className="mis-summary-row">
                      <span className="mis-summary-row__label">Difficulty</span>
                      <span className="mis-summary-row__value">{difficulty || "Not selected"}</span>
                    </div>
                    <Separator />
                    <div className="mis-summary-row">
                      <span className="mis-summary-row__label">Duration</span>
                      <span className="mis-summary-row__value">{duration ? `${duration} mins` : "Not selected"}</span>
                    </div>
                    <Separator />
                    <div className="mis-summary-row">
                      <span className="mis-summary-row__label">Resume</span>
                      <span className="mis-summary-row__value">
                        {resumeFile ? (
                          <span className="mis-summary-row__uploaded">
                            <CheckCircle size={12} /> Uploaded
                          </span>
                        ) : "Not uploaded"}
                      </span>
                    </div>
                  </div>

                  {error && (
                    <div className="mis-form__error">
                      <AlertCircle size={14} />
                      <span>{error}</span>
                    </div>
                  )}

                  <AppButton
                    className="mis-summary-sidebar__btn"
                    disabled={!canStart || loading}
                    onClick={handlePreCheckOpen}
                  >
                    {loading ? (
                      <><Spinner size="sm" animation="border" /> Starting...</>
                    ) : (
                      <><Play size={16} /> Start Interview</>
                    )}
                  </AppButton>
                  <p className="mis-summary-sidebar__note">
                    AI will ask questions based on your configuration and resume
                  </p>
                </AppCardContent>
              </AppCard>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="history">
          <HistoryTabContent
            historyLoading={historyLoading}
            history={history}
            navigate={navigate}
          />
        </TabsContent>
      </Tabs>

      {/* Pre-Interview Check Modal */}
      {showPreCheck && (
        <div className="mis-code-overlay">
          <div className="mis-precheck-modal">
            <div className="mis-precheck-modal__header">
              <h3 className="mis-precheck-modal__title">Pre-Interview Check</h3>
              <button type="button" className="mis-precheck-modal__close" onClick={() => { stopMicMonitor(); setShowPreCheck(false); }}>
                <X size={18} />
              </button>
            </div>
            <p className="mis-precheck-modal__desc">
              Verifying your setup before starting the interview...
            </p>
            <div className="mis-precheck-modal__list">
              {Object.entries(preChecks).map(([key, check]) => (
                <div key={key} className={`mis-precheck-item mis-precheck-item--${check.status}`}>
                  <div className="mis-precheck-item__icon">
                    {check.status === "checking" && <Loader2 size={18} className="mis-spin" />}
                    {check.status === "passed" && <CheckCircle size={18} />}
                    {check.status === "failed" && <AlertCircle size={18} />}
                    {check.status === "pending" && (
                      { microphone: <Mic size={18} />, internet: <Wifi size={18} />, browser: <Globe size={18} /> }[key]
                    )}
                  </div>
                  <div className="mis-precheck-item__info">
                    <span className="mis-precheck-item__label">{check.label}</span>
                    {check.detail && <span className="mis-precheck-item__detail">{check.detail}</span>}
                  </div>
                </div>
              ))}
            </div>

            {/* Device Selection — Teams-style */}
            {preCheckDone && microphones.length > 0 && (
              <div className="mis-precheck-devices">
                <h4 className="mis-precheck-devices__title">Audio Devices</h4>
                <div className="mis-precheck-devices__row">
                  <div className="mis-precheck-devices__field">
                    <label className="mis-precheck-devices__label">
                      <Mic size={14} /> Microphone
                    </label>
                    <div className="mis-precheck-devices__select-wrap">
                      <select
                        className="mis-precheck-devices__select"
                        value={selectedMic}
                        onChange={(e) => setSelectedMic(e.target.value)}
                      >
                        {microphones.map((d) => (
                          <option key={d.deviceId} value={d.deviceId}>
                            {d.label || `Microphone (${d.deviceId.slice(0, 8)})`}
                          </option>
                        ))}
                      </select>
                      <ChevronDown size={14} className="mis-precheck-devices__chevron" />
                    </div>
                    {/* Live mic level meter */}
                    <MicLevelBar level={micLevel} />
                  </div>
                  {speakers.length > 0 && (
                    <div className="mis-precheck-devices__field">
                      <label className="mis-precheck-devices__label">
                        <Volume2 size={14} /> Speaker
                      </label>
                      <div className="mis-precheck-devices__select-wrap">
                        <select
                          className="mis-precheck-devices__select"
                          value={selectedSpeaker}
                          onChange={(e) => setSelectedSpeaker(e.target.value)}
                        >
                          {speakers.map((d) => (
                            <option key={d.deviceId} value={d.deviceId}>
                              {d.label || `Speaker (${d.deviceId.slice(0, 8)})`}
                            </option>
                          ))}
                        </select>
                        <ChevronDown size={14} className="mis-precheck-devices__chevron" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {preCheckDone && (
              <div className="mis-precheck-modal__actions">
                {preCheckAllPassed ? (
                  <AppButton
                    className="mis-precheck-modal__start"
                    onClick={() => { stopMicMonitor(); setShowPreCheck(false); handleStartInterview(); }}
                    disabled={loading}
                  >
                    {loading ? <><Spinner size="sm" animation="border" /> Starting...</> : <><Play size={16} /> Start Interview</>}
                  </AppButton>
                ) : (
                  <>
                    <AppButton variant="secondary" onClick={runPreChecks}>
                      Retry Checks
                    </AppButton>
                    <AppButton
                      onClick={() => { stopMicMonitor(); setShowPreCheck(false); handleStartInterview(); }}
                      disabled={loading}
                    >
                      Start Anyway
                    </AppButton>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MockInterviewSetup;
